// src/schemas/api/internal/responses/colonel-customer-sessions.ts
//
// Per-customer colonel session view (spec docs/specs/colonel-ui/40-*).
//
// This is the SIDECAR-backed, per-customer companion to colonel-sessions.ts (the
// GLOBAL console). It is fed by Onetime::SessionMetadata#safe_dump — a POSITIVE
// allow-list that is the feature's security boundary: there is NO token, NO
// decrypted payload, NO email/secret field on the model, so none can appear here
// and the frontend physically cannot render one.
//
//   - ListCustomerSessions   → GET    /api/colonel/users/:user_id/sessions
//   - RevokeCustomerSession  → DELETE /api/colonel/users/:user_id/sessions/:session_handle
//
// Sessions are identified by session_handle, a non-reversible digest of the raw
// session id (finding F-01). The raw sid is the live session cookie / Redis blob
// key, so it is never sent to the client; the handle round-trips list → revoke.
//
// Shape verified VERBATIM against the SessionMetadata safe_dump_fields allow-list
// (lib/onetime/models/session_metadata.rb) and the logic adapters
// (apps/api/colonel/logic/colonel/{list_customer_sessions,revoke_customer_session}.rb).
// Epoch fields (created_at / last_activity_at) arrive as bare Unix-second numbers
// and are kept numeric. user_id is the customer EXTERNAL id (extid, 'ur...').

import { createApiResponseSchema } from '@/schemas/api/base';
import { sessionHandleSchema } from './session-handle';
import { z } from 'zod';

// The handle definition moved to ./session-handle when #4330 gave the GLOBAL
// console handles too — one regex, one tripwire, two contracts. Re-exported so
// existing importers of this module are untouched.
export { sessionHandleSchema };

// ============================================================================
// ListCustomerSessions — one customer's session rows
// ============================================================================

/**
 * A single per-customer session row — the SessionMetadata safe_dump shape
 * verbatim. session_handle is the non-reversible identifier (F-01) the revoke
 * endpoint accepts; the raw session id never crosses the API. Every field except
 * session_handle/user_id is nullable: org_id is the
 * active organization objid, resolved per write (null if the customer has no
 * org); ip_address/user_agent are copied as-is from the (already Otto-masked)
 * session data; auth_method is the primary login method stamped at auth time
 * ('password' | 'email_auth' | 'webauthn' | 'omniauth' | null for legacy);
 * mfa_used is a nullable boolean pending an enrichment path. geo_country is
 * the Otto-derived country code, or null when unknown (Otto's '**' sentinel is
 * normalized to null server-side and never crosses the API) — optional so a
 * mid-deploy response from an older backend (which omits the key) still parses
 * instead of dropping the whole session list; the UI renders absent/null as
 * "Unknown". There is NO email/token field — that absence is the security
 * guarantee.
 */
export const adminCustomerSessionSchema = z.object({
  session_handle: sessionHandleSchema,
  user_id: z.string(),
  org_id: z.string().nullable(),
  created_at: z.number().nullable(),
  last_activity_at: z.number().nullable(),
  ip_address: z.string().nullable(),
  user_agent: z.string().nullable(),
  auth_method: z.string().nullable(),
  mfa_used: z.boolean().nullable(),
  geo_country: z.string().nullable().optional(),
});

/**
 * ListCustomerSessions `details`: the customer's session rows + a count.
 *
 * `current_session_handle` is the handle of the acting colonel's OWN request
 * session whenever it can be identified, regardless of whether it appears among
 * these rows. The UI matches it against the rows' session_handle: on a match
 * (colonel viewing their own customer detail) it badges that row and disables
 * its per-row revoke, since a self-revoke is a no-op (Rack re-persists the
 * current session's blob at the end of the same request). Null/absent only when
 * the current session can't be identified (e.g. a Hash session under JSON auth).
 */
export const colonelCustomerSessionsDetailsSchema = z.object({
  sessions: z.array(adminCustomerSessionSchema),
  count: z.number(),
  current_session_handle: sessionHandleSchema.nullable().optional(),
});

// ============================================================================
// RevokeCustomerSession — guarded revoke ack
// ============================================================================

/** RevokeCustomerSession `record`: the revoked session's handle + a revoked flag. */
export const colonelCustomerSessionRevokeRecordSchema = z.object({
  session_handle: sessionHandleSchema,
  revoked: z.boolean(),
});

/** RevokeCustomerSession `details`: a human-readable ack message. */
export const colonelCustomerSessionRevokeDetailsSchema = z.object({
  message: z.string(),
});

// ============================================================================
// RevokeAllCustomerSessions — offboarding / takeover bulk revoke ack
// ============================================================================

/**
 * RevokeAllCustomerSessions `record`: the total lockout, with kill counts so the
 * operator sees how complete the revoke was. `untracked_deleted` is the number of
 * killed blobs the sidecar index did NOT know about (pre-sidecar sessions the
 * best-effort scan swept up); `rodauth_rows_deleted` is 0 in simple mode / when no
 * auth account maps to the customer. `scan_capped` is true when the untracked
 * sweep hit its scan cap — tracked sessions are always killed, but an untracked
 * pre-sidecar session MAY have been missed and the operator should be warned.
 */
export const colonelCustomerSessionRevokeAllRecordSchema = z.object({
  revoked: z.boolean(),
  blobs_deleted: z.number(),
  untracked_deleted: z.number(),
  rodauth_rows_deleted: z.number(),
  scan_capped: z.boolean(),
});

/** RevokeAllCustomerSessions `details`: a human-readable ack message. */
export const colonelCustomerSessionRevokeAllDetailsSchema = z.object({
  message: z.string(),
});

// ============================================================================
// Type Exports
// ============================================================================

export type AdminCustomerSession = z.infer<typeof adminCustomerSessionSchema>;
export type ColonelCustomerSessionRevokeRecord = z.infer<
  typeof colonelCustomerSessionRevokeRecordSchema
>;
export type ColonelCustomerSessionRevokeAllRecord = z.infer<
  typeof colonelCustomerSessionRevokeAllRecordSchema
>;

// Wrapped response schemas for the per-customer sessions view.
// Internal-only; consumed by the Vue admin console, never exposed publicly.

// GET /api/colonel/users/:user_id/sessions → ListCustomerSessions
export const colonelCustomerSessionsResponseSchema = createApiResponseSchema(
  z.object({}),
  colonelCustomerSessionsDetailsSchema
);

// DELETE /api/colonel/users/:user_id/sessions/:session_handle → RevokeCustomerSession
export const colonelCustomerSessionRevokeResponseSchema = createApiResponseSchema(
  colonelCustomerSessionRevokeRecordSchema,
  colonelCustomerSessionRevokeDetailsSchema
);

// POST /api/colonel/users/:user_id/sessions/revoke-all → RevokeAllCustomerSessions
export const colonelCustomerSessionRevokeAllResponseSchema = createApiResponseSchema(
  colonelCustomerSessionRevokeAllRecordSchema,
  colonelCustomerSessionRevokeAllDetailsSchema
);

export type ColonelCustomerSessionsResponse = z.infer<
  typeof colonelCustomerSessionsResponseSchema
>;
export type ColonelCustomerSessionRevokeResponse = z.infer<
  typeof colonelCustomerSessionRevokeResponseSchema
>;
export type ColonelCustomerSessionRevokeAllResponse = z.infer<
  typeof colonelCustomerSessionRevokeAllResponseSchema
>;
